import { useMemo } from "react";

import {
  type RuleGraphDto,
  stampOrgExclusionsOnGraph,
} from "@intelligems/ig-types/dtos/agentic-logic/rule-graph";

import { useAgenticLogicExclusions } from "@src/api/agentic-logic/get-agentic-logic-exclusions";

/**
 * The graph with its Exclusions card reading the org's CURRENT exclusions.
 *
 * Every producer stamps the step with placeholder copy, because none of them
 * holds org state: a model reads source, a template reads params. The org's
 * exclusions are a singleton the page already caches, so the canvas overlays
 * them here, at read — and follows an Exclusions-tab edit without a republish,
 * which is also when serving picks it up. Until the exclusions arrive the
 * producer's placeholder stands rather than a wrong "nothing excluded".
 */
export const useOrgExclusionsGraph = (
  graph: RuleGraphDto | null
): RuleGraphDto | null => {
  const exclusions = useAgenticLogicExclusions().data?.exclusions ?? null;
  // Explicit, despite the project's general React Compiler preference: the
  // stamped graph is consumed by identity, so a fresh object on every
  // unrelated render would restart the canvas. Keyed on both inputs so a
  // graph that goes non-null -> null -> non-null re-stamps rather than
  // serving a stale overlay.
  return useMemo(
    () =>
      graph === null || exclusions === null
        ? graph
        : stampOrgExclusionsOnGraph(graph, exclusions),
    [graph, exclusions]
  );
};
